import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { subscribeToOrdersUpdates } from '../../services/socket';
import { Download, Printer, Loader2, X } from 'lucide-react';
import { exportSalesToExcel, exportOfferSalesToExcel } from '../../utils/excelExport';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency, formatSkuDisplay } from '../../utils/format';
import { fetchProductSalesForRange } from '../../utils/insightsSales';
import { printHtml } from '../../utils/printing';
import { getTokens } from '../../utils/tokens';
import {
  PageHeader,
  StatStrip,
  GhostBtn,
  PrimaryBtn,
  Toolbar,
  FilterChip,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
  MethodPill,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const Sales = () => {
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);
  const isDark = mode === 'black' || mode === 'dark';

  const [data, setData] = useState([]);
  // Server-computed totals cover the whole result set, not just the fetched page.
  const [salesTotals, setSalesTotals] = useState({
    revenue: 0,
    profit: 0,
    sold_quantity: 0,
    product_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category_id: 'all',
    session_number: '',
    product_search: '',
    offer_search: '',
  });
  const [salesTab, setSalesTab] = useState('product');

  const loadCategories = async () => {
    try {
      const res = await apiService.getCategories();
      setCategories(Array.isArray(res?.data) ? res.data : res ?? []);
    } catch (e) {
      console.error('Failed to load categories', e);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    fetchSales();
  }, [filters, salesTab]);

  useEffect(() => {
    const unsubOrders = subscribeToOrdersUpdates(fetchSales);
    return () => { unsubOrders(); };
  }, [filters, salesTab]);

  const fetchSales = async () => {
    setLoading(true);
    setError(null);
    try {
      if (salesTab === 'offer') {
        const { start_date, end_date, session_number, offer_search } = filters;
        const res = await apiService.getOfferSales({
          limit: 5000,
          start_date: start_date || undefined,
          end_date: end_date || undefined,
          session_number: session_number || undefined,
          offer_search: offer_search || undefined,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const totals = res?.totals || {
          revenue: rows.reduce((s, r) => s + parseFloat(r.revenue || 0), 0),
          profit: rows.reduce((s, r) => s + parseFloat(r.profit || 0), 0),
          sold_quantity: rows.reduce((s, r) => s + parseFloat(r.sold_quantity || 0), 0),
          product_count: rows.length,
        };
        const offerCount = res?.meta?.offer_count ?? res?.meta?.product_count ?? rows.length;
        setData(rows);
        setSalesTotals({
          ...totals,
          product_count: offerCount,
        });
      } else {
        const range = {
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
        };
        const { rows, totals } = await fetchProductSalesForRange(apiService, range, {
          category_id: filters.category_id,
          session_number: filters.session_number,
          product_search: filters.product_search,
        });
        setData(rows);
        setSalesTotals(totals || { revenue: 0, profit: 0, sold_quantity: 0, product_count: rows.length });
      }
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadSales'));
      console.error('Failed to fetch sales', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category_id: 'all',
      session_number: '',
      product_search: '',
      offer_search: '',
    });
  };

  const hasActiveFilters =
    !!filters.start_date ||
    !!filters.end_date ||
    filters.category_id !== 'all' ||
    filters.session_number.trim() !== '' ||
    filters.product_search.trim() !== '' ||
    filters.offer_search.trim() !== '';

  const getUnitLabel = (unitType) => {
    if (!unitType || unitType === 'piece') return t('pos.pcs');
    if (unitType === 'kg') return t('pos.kg');
    return unitType;
  };

  /** Qty column: show number only when API omits `unit_type` (no default "pcs"). */
  const formatSoldQtyWithOptionalUnit = (row) => {
    const qtyStr = parseFloat(row.sold_quantity || 0).toLocaleString();
    const raw = row.unit_type;
    if (raw == null || String(raw).trim() === '') return qtyStr;
    return `${qtyStr} ${getUnitLabel(raw)}`;
  };

  const getCategoryName = (id) => {
    if (!id || id === 'all') return t('common.all');
    const c = categories.find((x) => String(x.id) === String(id));
    return c?.name || t('common.all');
  };

  const totalPages = Math.ceil((data || []).length / itemsPerPage);
  const currentData = React.useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return (data || []).slice(indexOfFirstItem, indexOfLastItem);
  }, [data, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, salesTab]);

  const handleDownloadExcel = async () => {
    if (data.length === 0) {
      showToast(t('sales.noSalesData'), "warning");
      return;
    }
    setExporting(true);
    try {
      const labels = t('sales.excelExport');
      const excelLabels = typeof labels === 'object' && labels !== null ? labels : {};
      // datetime-local values contain ':' which is invalid in filenames.
      const safe = (v) => String(v || '').replace(/[:T]/g, '-');
      if (salesTab === 'offer') {
        const filename = `offer-sales-${safe(filters.start_date)}-to-${safe(filters.end_date)}.xlsx`;
        await exportOfferSalesToExcel(data, filename, {
          ...excelLabels,
          sku: t('offers.sku'),
        });
      } else {
        const filename = `sales-${safe(filters.start_date)}-to-${safe(filters.end_date)}.xlsx`;
        const categoryColorByName = Object.fromEntries(
          categories
            .filter((c) => c && String(c.name || '').trim() && c.color)
            .map((c) => [String(c.name).trim(), String(c.color).trim()])
        );
        await exportSalesToExcel(data, filename, {
          ...excelLabels,
          formatUnitType: getUnitLabel,
          categoryColorByName,
        });
      }
    } catch (err) {
      showToast(err?.message || t('common.error'), "error");
    } finally {
      setExporting(false);
    }
  };

  const printSalesReport = () => {
    const isRTLLocal = (typeof localStorage !== 'undefined' ? localStorage.getItem('language') : 'en') === 'ar';
    const fontLink = isRTLLocal ? '<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">' : '';
    const totalUnits = data.reduce((s, r) => s + parseFloat(r.sold_quantity || 0), 0);
    const totalRevenue = data.reduce((s, r) => s + parseFloat(r.revenue || 0), 0);
    const totalProfit = data.reduce((s, r) => s + parseFloat(r.profit || 0), 0);

    const filterLines = salesTab === 'offer'
      ? [
          `${t('common.from')}: ${(filters.start_date || '').replace('T', ' ')}`,
          `${t('common.to')}: ${(filters.end_date || '').replace('T', ' ')}`,
          `${t('sessions.sessionNo')}: ${filters.session_number.trim() || t('common.all')}`,
          `${t('sales.searchOffer')}: ${filters.offer_search.trim() || t('common.all')}`,
        ]
      : [
          `${t('common.from')}: ${(filters.start_date || '').replace('T', ' ')}`,
          `${t('common.to')}: ${(filters.end_date || '').replace('T', ' ')}`,
          `${t('inventory.category')}: ${getCategoryName(filters.category_id)}`,
          `${t('sessions.sessionNo')}: ${filters.session_number.trim() || t('common.all')}`,
          `${t('sales.searchProduct')}: ${filters.product_search.trim() || t('common.all')}`,
        ];

    const rowsHtml = data
      .map(
        (row) => `
      <tr>
        <td class="cell">${((salesTab === 'offer' ? row.offer_name : row.product_name) || t('inventory.uncategorized')).replace(/</g, '&lt;')}</td>
        <td class="cell num">${formatSoldQtyWithOptionalUnit(row)}</td>
        <td class="cell num">${t('currency.egp')} ${formatCurrency(row.revenue)}</td>
        <td class="cell num">${t('currency.egp')} ${formatCurrency(row.profit)}</td>
      </tr>
    `
      )
      .join('');

    const html = `
    <html>
      <head>
        <title>${t('sales.title')}</title>
        ${fontLink}
        <style>
          @page { size: 80mm auto; margin: 0; }
          body { font-family: 'Courier New', monospace; font-size: 10px; margin: 0; padding: 0; color: #000; }
          .receipt { width: 100%; box-sizing: border-box; padding: 4px 16px; }
          .header { text-align: center; margin-bottom: 6px; }
          .title { font-size: 11px; font-weight: bold; margin-bottom: 2px; }
          .filters { margin: 4px 0; padding: 4px; border: 1px solid #333; font-size: 10px; }
          .filters-title { font-weight: bold; margin-bottom: 2px; }
          .filter-line { margin: 1px 0; font-weight: 600; }
          table { width: 100%; border-collapse: collapse; margin-top: 4px; table-layout: fixed; }
          th, td { border: 1px solid #333; padding: 2px 3px; text-align: left; font-size: 9px; word-break: break-word; }
          th { background: #000 !important; color: #fff !important; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          td { font-weight: 600; }
          .num { text-align: right; }
          .summary { margin-top: 6px; padding: 4px; border: 1px solid #333; font-weight: bold; font-size: 10px; }
          .summary-line { margin: 1px 0; }
          @media print { body { margin: 0; padding: 0; } }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <div class="title">${t('sales.title')}</div>
            <div style="font-size: 10px; font-weight: 600;">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
          </div>
          <div class="filters">
            <div class="filters-title">${t('common.filter')}</div>
            ${filterLines.map((l) => `<div class="filter-line">${l.replace(/</g, '&lt;')}</div>`).join('')}
          </div>
          <table>
            <colgroup>
              <col style="width: 45%">
              <col style="width: 18%">
              <col style="width: 19%">
              <col style="width: 18%">
            </colgroup>
            <thead>
              <tr>
                <th>${salesTab === 'offer' ? t('sales.offerName') : t('inventory.productName')}</th>
                <th>${salesTab === 'offer' ? t('sales.bundlesSold') : t('sales.soldQuantity')}</th>
                <th>${t('sales.revenue')}</th>
                <th>${t('sales.revenueProfit')}</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div class="summary">
            <div class="summary-line">${t('sales.totalUnitsSold')}: ${totalUnits.toLocaleString()}</div>
            <div class="summary-line">${t('sales.totalRevenue')}: ${t('currency.egp')} ${formatCurrency(totalRevenue)}</div>
            <div class="summary-line">${t('sales.revenueProfit')}: ${t('currency.egp')} ${formatCurrency(totalProfit)}</div>
          </div>
        </div>
      </body>
    </html>
    `;

    const htmlWithDir = isRTLLocal
      ? html.replace('<body>', '<body dir="rtl" style="text-align: right; font-family: \'Cairo\', \'Courier New\', monospace;">')
      : html;
    printHtml(htmlWithDir);
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalRevenue = parseFloat(salesTotals.revenue || 0);
  const totalUnits = parseFloat(salesTotals.sold_quantity || 0);
  const totalProfit = parseFloat(salesTotals.profit || 0);
  const avgBasket = data.length > 0 ? totalRevenue / data.length : 0;

  // ── Category filter options ────────────────────────────────────────────────
  const categoryOptions = [
    { value: 'all', label: t('common.all') },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = salesTab === 'offer'
    ? [
        { key: 'offer_name', label: t('sales.offerName') },
        { key: 'sku', label: t('offers.sku') },
        { key: 'sold_quantity', label: t('sales.bundlesSold') },
        { key: 'revenue', label: t('sales.revenue') },
        { key: 'profit', label: t('sales.revenueProfit') },
      ]
    : [
        { key: 'product_name', label: t('inventory.productName') },
        { key: 'sku', label: t('inventory.sku') },
        { key: 'category_name', label: t('inventory.category') },
        { key: 'sold_quantity', label: t('sales.soldQuantity') },
        { key: 'revenue', label: t('sales.revenue') },
        { key: 'profit', label: t('sales.revenueProfit') },
      ];

  const renderCell = (row, key) => {
    switch (key) {
      case 'offer_name':
        return (
          <span style={{ fontWeight: 500, color: tk.text }}>
            {row.offer_name || t('inventory.uncategorized')}
          </span>
        );
      case 'product_name':
        return (
          <span style={{ fontWeight: 500, color: tk.text }}>
            {row.product_name || t('inventory.uncategorized')}
          </span>
        );
      case 'sku': {
        const sku = formatSkuDisplay(row.sku);
        return (
          <span
            title={sku.title}
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11.5,
              color: tk.textMuted,
              direction: 'ltr',
              unicodeBidi: 'isolate',
              display: 'inline-block',
            }}
          >
            {sku.display}
          </span>
        );
      }
      case 'category_name':
        return (
          <span style={{ color: tk.textSecondary }}>
            {row.category_name || t('inventory.uncategorized')}
          </span>
        );
      case 'sold_quantity':
        return (
          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: tk.text }}>
            {formatSoldQtyWithOptionalUnit(row)}
          </span>
        );
      case 'revenue':
        return <MonoMoney amount={row.revenue} tk={tk} size={12.5} weight={600} color={tk.successText} />;
      case 'profit':
        return <MonoMoney amount={row.profit} tk={tk} size={12.5} weight={600} />;
      default:
        return null;
    }
  };

  // ── Chart style helpers ────────────────────────────────────────────────────
  const accentMain = isDark ? '#818cf8' : '#4f46e5';
  const gridColor = isDark ? '#2a2c36' : '#e7e7e3';
  const axisStyle = { fill: tk.textMuted, fontSize: 10 };
  const tooltipStyle = {
    contentStyle: {
      background: tk.surface,
      border: `1px solid ${tk.border}`,
      borderRadius: 8,
      color: tk.text,
      fontSize: 11,
    },
    labelStyle: { color: tk.textMuted, fontSize: 10 },
  };


  // ── Style helpers ──────────────────────────────────────────────────────────
  const fontFamily = language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif';

  const wrapperStyle = {
    background: tk.bg,
    color: tk.text,
    padding: 20,
    minHeight: '100%',
    fontFamily,
    overflowY: 'auto',
  };

  const salesFilterLabelStyle = {
    fontSize: 10.5,
    color: tk.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle} dir={isRTL ? 'rtl' : 'ltr'} className={`theme-${mode}`}>

      <PageHeader
        tk={tk}
        title={t('nav.sales') || t('sales.title')}
        subtitle={language === 'ar' ? 'الأداء عبر الوقت، حسب الفئة والمنتج' : 'Performance over time, by category & product'}
        actions={
          <>
            <GhostBtn tk={tk} onClick={printSalesReport} icon={Printer}>
              {t('common.print')}
            </GhostBtn>
            <GhostBtn
              tk={tk}
              onClick={handleDownloadExcel}
              icon={exporting ? Loader2 : Download}
            >
              {exporting ? t('monthlyClosing.downloading') : t('sales.downloadExcel')}
            </GhostBtn>
            <GhostBtn tk={tk} onClick={fetchSales}>
              {t('common.refresh')}
            </GhostBtn>
          </>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setSalesTab('product')}
          style={{
            height: 36, padding: '0 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            background: salesTab === 'product' ? tk.accent : tk.surfaceAlt,
            color: salesTab === 'product' ? '#fff' : tk.textSecondary,
            border: `1px solid ${salesTab === 'product' ? tk.accent : tk.border}`,
          }}
        >
          {t('sales.byProduct')}
        </button>
        <button
          type="button"
          onClick={() => setSalesTab('offer')}
          style={{
            height: 36, padding: '0 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            background: salesTab === 'offer' ? tk.accent : tk.surfaceAlt,
            color: salesTab === 'offer' ? '#fff' : tk.textSecondary,
            border: `1px solid ${salesTab === 'offer' ? tk.accent : tk.border}`,
          }}
        >
          {t('sales.byOffer')}
        </button>
      </div>

      {loading ? (
        <StatStripSkeleton tk={tk} count={4} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: t('sales.totalRevenue'), value: totalRevenue, money: true, positive: true },
            { label: t('sales.totalUnitsSold'), value: totalUnits.toLocaleString() },
            { label: t('sales.revenueProfit'), value: totalProfit, money: true },
            { label: salesTab === 'offer' ? t('nav.offers') : t('common.products'), value: String(salesTotals.product_count || data.length) },
          ]}
        />
      )}

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          color: tk.dangerText, fontSize: 12.5,
        }}>
          {t('common.error')}: {error}
        </div>
      )}

      {/* Charts row — top products + category revenue */}
      {/* Toolbar / Filters */}
      <Toolbar
        tk={tk}
        searchPlaceholder={salesTab === 'offer' ? t('sales.searchOffer') : t('inventory.searchProducts')}
        searchValue={salesTab === 'offer' ? filters.offer_search : filters.product_search}
        onSearchChange={(v) => setFilters((prev) => (
          salesTab === 'offer'
            ? { ...prev, offer_search: v }
            : { ...prev, product_search: v }
        ))}
        filters={
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
              <span style={salesFilterLabelStyle}>{t('sessions.sessionNo')}</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t('sessions.searchBySessionNo')}
                value={filters.session_number}
                onChange={(e) => setFilters((prev) => ({ ...prev, session_number: e.target.value.replace(/\D/g, '') }))}
                style={{
                  height: 34, padding: '0 10px', borderRadius: 8,
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
              <span style={salesFilterLabelStyle}>{t('common.from')}</span>
              <input
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                style={{
                  height: 34, width: '100%', boxSizing: 'border-box', padding: '0 10px', borderRadius: 8,
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
              <span style={salesFilterLabelStyle}>{t('common.to')}</span>
              <input
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                style={{
                  height: 34, width: '100%', boxSizing: 'border-box', padding: '0 10px', borderRadius: 8,
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none',
                }}
              />
            </div>
            {salesTab === 'product' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
              <span style={salesFilterLabelStyle}>{t('inventory.category')}</span>
              <FilterChip
                tk={tk}
                label={t('inventory.category')}
                value={filters.category_id}
                active={filters.category_id !== 'all'}
                onChange={(v) => setFilters((prev) => ({ ...prev, category_id: v }))}
                options={categoryOptions}
              />
            </div>
            )}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: 'flex-end',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  height: 34, padding: '0 10px', borderRadius: 8,
                  background: tk.dangerSoft, border: 'none',
                  color: tk.dangerText, fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={salesTab === 'offer' ? [3, 1.5, 1, 1.5, 1.5] : [3, 1.5, 1.5, 1, 1.5, 1.5]} />
      ) : (
        <DataTable
          tk={tk}
          columns={columns}
          rows={currentData}
          renderCell={renderCell}
          emptyMessage={t('sales.noSalesData')}
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default Sales;
